import type { ClassDefinition } from '../../schemas/classDefinition';
import { monacoAdvanced2to5Moves } from '../moves/monaco/advanced-2-5';
import { monacoAdvanced6to10Moves } from '../moves/monaco/advanced-6-10';
import { monacoStartingAutoMoves, monacoStartingChoiceMoves } from '../moves/monaco/initial';

/**
 * Definizione della classe **Monaco** (manuale V1, rr. 4395-4779).
 *
 * Settima classe trascritta. Pattern principali stress-testati:
 * - **Mossa-config *Sentieri del Monaco*** con 7 restrizioni strutturate
 *   (`MoveSchema.choices`, choiceCount 1) + nome dell'ordine in description
 *   (FI-18 strutturerà più avanti).
 * - **`MoveSchema.replaces` 2 casi consecutivi**: *Sii Come l'Acqua*
 *   sostituisce *Pace Interiore*; *Dono di Compassione* sostituisce *Atto
 *   Compassionevole*. Secondo uso strutturale dopo il Mago (FI-21).
 * - **`prerequisites.anyOf` cross-class** verso Mago: *Mastro Artigiano*
 *   richiede *Regola del Lavoro* OR `mago.laboratorio`.
 * - **3 Regole come gruppo di scelta iniziale** (`pickCount: 1` di 3, mosse
 *   trigger di prerequisiti per le mosse avanzate).
 *
 * **Punti notevoli**:
 * - Dado di danno **d6**, PF base 8, Carico base 9
 * - 5 archetipi razza-tematici
 * - 5 mosse iniziali automatiche + 3 mosse iniziali di scelta (`pickCount: 1`)
 * - 16 mosse avanzate L2-5, 14 mosse avanzate L6-10 (43 mosse totali)
 * - 4 template di Legami (= `maxBonds`, FI-3 rispettato)
 * - **Non casteggiante**: `spellcasting` resta `undefined`
 * - 4 categorie di Aspetto (Occhi/Testa/Vesti/Corpo) — la categoria "Testa"
 *   è specifica del Monaco (al posto di "Capelli" delle altre classi) perché
 *   include "Testa rasata" tra le opzioni.
 */
export const monacoClassDefinition: ClassDefinition = {
  id: 'monaco',
  name: 'Monaco',

  intro:
    "Li vedi, come onde in tempesta, agitarsi senza tregua, bramosi di uno scopo, di una direzione. Oggi inseguono il miraggio della gloria, domani la chimera dell'oro. Sempre alla ricerca di qualcosa che sfugge, senza una mappa per orientarsi nel viaggio della vita. Tu hai scelto la quiete del chiostro, il silenzio che nutre l'anima. Lì, tra le mura sacre, hai trovato la chiarezza che cercavi. I tuoi maestri ti hanno indicato il sentiero, un cammino lastricato di saggezza, ma anche irto di privazioni e *Regole*. Non solo *Meditazione* e studio, no. Nel *Monastero*, la vita è un intreccio di *ora*, *labora* et *pugna*, preghiera, lavoro e difesa della Via. Ogni giorno una lezione di disciplina, un passo verso la padronanza del corpo, della mente e della volontà. Hai imparato a canalizzare l'energia interiore, trasformando la meditazione in forza, la quiete in azione. Che tu abbia trovato la tua strada nella furia della *Battaglia*, nell'umile dedizione al *Lavoro*, o nella compassionevole grazia della *Pietà*, adesso conosci la meta e la strada per raggiungerla.\n\nEd ora, con il cuore saldo e la mente illuminata, sei pronto a varcare di nuovo la soglia verso quel mondo di incertezze che ti eri lasciato alle spalle. Che gli altri ti giudichino un asceta, un uomo legato al passato. Quando le ombre della confusione li avvolgeranno, quando la loro carne sarà debole e il loro spirito vacillante, a chi chiederanno consiglio? A chi chiederanno forza? A te, inevitabilmente. Perché mentre loro inseguono fuochi fatui nel crepuscolo, tu cammini imperturbabile sul sentiero dell'illuminazione, e la tua sola presenza può trasformare il loro smarrimento in una nuova consapevolezza.",

  // Manuale V1, rr. 4402-4410
  suggestedNames: [
    // Umano
    'Sig',
    'Kaze',
    'Tara',
    'Jade',
    'Sakura',
    'Konrad',
    'Fei',
    'Dom',
    'Patrick',
    'Chizu',
    'Mor',
    'Manea',
    // Elfo
    'Taridin',
    'Nyegilras',
    'Finror',
    'Finaera',
    'Carorle',
    'Leethasfin',
    'Andtha',
    'Cirda',
    'Te-el',
    // Nano
    'Abhin',
    'Daniki',
    'Aras',
    'Mavi',
    'Kala',
    'Qatji',
    'Fehri',
    'Yala',
    'Zagi',
    'Midalf',
    'Gilin',
    'Hofni',
    // Halfling
    'Ocmel',
    'Bellding',
    'Codaddy',
    'Barnabas',
    'Ilbucca',
    'Fifast',
    'Liali',
    'Modi',
    'Mirela',
    // Mezz'orco
    'Aggra',
    'Unag',
    'Triggzu',
    'Guldish',
    'Ogrimb',
    'Megga',
    'Hed',
    'Ubei',
    'Shakra',
  ],

  suggestedTitles: [],

  // Manuale V1, rr. 4412-4418. Categoria "Testa" (specifica del Monaco).
  aspetti: [
    {
      label: 'Occhi',
      options: ['Saggi', 'Imperscrutabili', 'Profondi'],
    },
    {
      label: 'Testa',
      options: ['Testa rasata', 'Lunga treccia', 'Tonsura'],
    },
    {
      label: 'Vesti',
      options: ['Tunica consunta', 'Saio grezzo', 'Paramenti eleganti'],
    },
    {
      label: 'Corpo',
      options: ['Tonico', 'Tatuato', 'Emaciato'],
    },
  ],

  // Manuale V1, rr. 4420-4426
  damageDie: 'd6',
  baseHp: 8,
  baseLoad: 9,

  // Manuale V1, rr. 4428-4436
  alignmentPrecepts: {
    buono: 'Aiuti qualcuno o qualcosa a trovare la sua illuminazione.',
    legale: "Spingi qualcun altro alla disciplina o all'autocontrollo.",
    neutrale: 'Guidi qualcuno o qualcosa da un estremo a una via di mezzo.',
    caotico:
      "Sfidi apertamente una tradizione, un'autorità o un dogma consolidato, anche a costo di gravi conseguenze.",
    malvagio: 'Sacrifichi il benessere altrui per accelerare la tua ascesa.',
  },

  // Manuale V1, rr. 4457-4459. **maxBonds: 4** (4 templates ↔ 4, FI-3 rispettato).
  bondTemplates: [
    'Le tentazioni incarnate da _____ mettono alla prova la mia disciplina. Devo rimanere saldo.',
    'La mia filosofia non riesce a spiegare le vie di _____.',
    '_____ è tormentato e ha bisogno del mio aiuto per trovare la pace interiore.',
    'Ho insegnato a _____ un segreto del mio ordine. Sarà fiducia ben riposta?',
  ],
  maxBonds: 4,

  // Manuale V1, rr. 4461-4470
  archetypes: [
    {
      id: 'umano',
      name: 'Umano',
      associatedRace: 'umano',
      description:
        "Quando prendi le mosse *La Via dell'Eremita* o *La Strada dell'Eremita*, puoi sempre scegliere mosse del Druido o dello Sciamano.",
      grantedMoveId: 'monaco.archetipo-umano',
    },
    {
      id: 'elfo',
      name: 'Elfo',
      associatedRace: 'elfo',
      description:
        'Quando usi la saggezza che hai maturato durante la tua lunga vita come leva per *parlamentare*, puoi tirare+**SAG** invece che +**CAR**.',
      grantedMoveId: 'monaco.archetipo-elfo',
    },
    {
      id: 'nano',
      name: 'Nano',
      associatedRace: 'nano',
      description:
        'Quando rinunci a un tesoro di grande valore, a una ricca ricompensa o a un intenso piacere materiale, segna 1 PE (al massimo una volta per sessione).',
      grantedMoveId: 'monaco.archetipo-nano',
    },
    {
      id: 'halfling',
      name: 'Halfling',
      associatedRace: 'halfling',
      description:
        'Quando *intraprendi un viaggio pericoloso* e agisci come quartiermastro, hai sempre successo come se avessi tirato un 10+.',
      grantedMoveId: 'monaco.archetipo-halfling',
    },
    {
      id: 'mezzorco',
      name: "Mezz'orco",
      associatedRace: 'mezzorco',
      description: "Ignori l'etichetta *impatto* su qualsiasi attacco che subisci.",
      grantedMoveId: 'monaco.archetipo-mezzorco',
    },
  ],

  startingMoves: monacoStartingAutoMoves.map((m) => m.id),

  // Manuale V1, r. 4520: "Inoltre, scegli una *Regola*". Le 3 Regole sono
  // mosse-trigger di prerequisiti per molte mosse avanzate.
  startingChoiceGroups: [
    {
      id: 'regola',
      name: 'Regola',
      description:
        "Scegli una delle tre Regole del Monaco. Le Regole sono prerequisiti per molte mosse avanzate (Battaglia → *Stile senz'Armi*, *Spirito Marziale*; Lavoro → *Mastro Speziale*, *Mastro Artigiano*; Pietà → *Atto Compassionevole*).",
      moveOptions: monacoStartingChoiceMoves.map((m) => m.id),
      pickCount: 1,
      expandableInAdvanced: false,
    },
  ],

  advancedMoves2to5: monacoAdvanced2to5Moves.map((m) => m.id),
  advancedMoves6to10: monacoAdvanced6to10Moves.map((m) => m.id),

  // Il Monaco non è casteggiante: la sua "magia" è la disciplina della
  // Meditazione e della Forza di Volontà, ma il sistema *Lanciare
  // Incantesimi* / *Mana* / Tradizioni Sacre non si applica.
  // spellcasting resta undefined.

  // Manuale V1, rr. 4439-4455
  startingEquipment: {
    always: [
      {
        id: 'ciotola-per-le-offerte',
        name: 'Ciotola per le offerte',
        weight: 0,
        tags: [],
        description:
          'Puoi ottenere 1 *razione* al giorno mendicando in un insediamento o da una persona generosa.',
      },
      {
        id: 'razioni-da-dungeon',
        name: 'Razioni da Dungeon',
        weight: 1,
        uses: 5,
        tags: ['razione'],
      },
    ],
    choices: [
      {
        id: 'arma-iniziale',
        prompt: "Scegli un'opzione per le tue armi",
        pickCount: 1,
        options: [
          {
            id: 'bastone-ferrato',
            label: 'Bastone ferrato',
            items: [
              {
                id: 'bastone-ferrato',
                name: 'Bastone ferrato',
                weight: 2,
                tags: ['lunga', 'a due mani'],
                damage: '+1 danni',
              },
            ],
          },
          {
            id: 'bastone-da-passeggio-falcetto',
            label: 'Bastone da passeggio + Falcetto',
            items: [
              {
                id: 'bastone-da-passeggio',
                name: 'Bastone da passeggio',
                weight: 1,
                tags: ['media', 'a due mani'],
              },
              {
                id: 'falcetto',
                name: 'Falcetto',
                weight: 1,
                tags: ['corta'],
              },
            ],
          },
          {
            id: 'pozione-curativa',
            label: 'Pozione Curativa',
            items: [
              {
                id: 'pozione-curativa',
                name: 'Pozione Curativa',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
      {
        id: 'extra-iniziale',
        prompt: "Scegli un'opzione tra le seguenti",
        pickCount: 1,
        options: [
          {
            id: 'attrezzatura-da-avventuriero',
            label: 'Attrezzatura da Avventuriero',
            items: [
              {
                id: 'attrezzatura-da-avventuriero',
                name: 'Attrezzatura da Avventuriero',
                weight: 2,
                uses: 5,
                tags: [],
              },
            ],
          },
          {
            id: 'bende',
            label: 'Bende',
            items: [
              {
                id: 'bende',
                name: 'Bende',
                weight: 0,
                uses: 3,
                tags: ['lento'],
              },
            ],
          },
          {
            id: 'antitossina',
            label: 'Antitossina',
            items: [
              {
                id: 'antitossina',
                name: 'Antitossina',
                weight: 0,
                uses: 1,
                tags: [],
              },
            ],
          },
        ],
      },
    ],
  },
};
